import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
    Building2, CreditCard, Save, Edit, X, 
    Banknote, Smartphone, CheckCircle
} from 'lucide-react';

const PaymentDetailsForm = ({ paymentDetails, onSubmit, onCancel, editing }) => {
    // Initialize state with provided payment details (if any)
    const initial = {
        account_type: paymentDetails?.account_type || 'bank',
        bank_name: paymentDetails?.bank_name || '',
        bank_branch: paymentDetails?.bank_branch || '',
        account_number: paymentDetails?.account_number || '',
        account_name: paymentDetails?.account_name || '',
        network_name: paymentDetails?.network_name || '',
        contact: paymentDetails?.contact || '',
        slug: paymentDetails?.slug || ''
    }
    const [formData, setFormData] = useState(initial);

    const banksInUganda = [
        "Stanbic Bank Uganda",
        "Centenary Bank",
        "DFCU Bank",
        "ABSA Bank Uganda",
        "Equity Bank Uganda",
        "Bank of Africa Uganda",
        "Standard Chartered Bank Uganda",
        "Housing Finance Bank",
        "Cairo Bank Uganda",
        "PostBank Uganda",
        "Uganda Development Bank",
        "Tropical Bank",
        "Exim Bank Uganda",
        "Finance Trust Bank",
    ];

    // Handle account type change
    const handleAccountTypeChange = (event) => {
        const { value } = event.target;
        setFormData(prevState => ({
            ...prevState,
            account_type: value
        }));
    };

    // Handle input change for other fields
    const handleInputChange = (event) => {
        const { name, value } = event.target;
        setFormData(prevState => ({
            ...prevState,
            [name]: value
        }));
    };

    // Handle form submission
    const handleSubmit = (event) => {
        event.preventDefault();
        if(formData.account_type === 'bank'){
            if (!formData.account_number || !formData.account_name ||  !formData.bank_name) {
                alert("Please fill in all required fields.");
                return;
            }
        }
        if(formData.account_type === 'mobile_money'){
            if (!formData.network_name || !formData.contact) {
                alert("Please fill in all required fields.");
                return;
            }
        }
        onSubmit(formData);
    };

    return (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-500 rounded-xl flex items-center justify-center">
                    {formData.account_type === 'bank' ? (
                        <Building2 className="w-5 h-5 !text-white" />
                    ) : (
                        <CreditCard className="w-5 h-5 !text-white" />
                    )}
                </div>
                <div>
                    <h3 className="text-xl font-bold text-gray-800">
                        {editing ? 'Edit' : 'Add'} {formData.account_type === 'bank' ? 'Bank' : 'Mobile Money'} Account
                    </h3>
                    <p className="text-gray-600">Enter your account details for payments</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Account Type Selection */}
                <div className="space-y-3">
                    <label className="block text-sm font-semibold text-gray-700">
                        Account Type <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <motion.button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, account_type: 'bank' }))}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 ${
                                formData.account_type === 'bank'
                                    ? 'border-teal-500 bg-teal-50 text-teal-700'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-teal-200'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <Building2 className="w-5 h-5" />
                            <span className="font-medium">Bank Account</span>
                        </motion.button>
                        
                        <motion.button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, account_type: 'mobile_money' }))}
                            className={`p-4 rounded-xl border-2 transition-all duration-200 flex items-center gap-3 ${
                                formData.account_type === 'mobile_money'
                                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                                    : 'border-gray-200 bg-white text-gray-600 hover:border-orange-200'
                            }`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <CreditCard className="w-5 h-5" />
                            <span className="font-medium">Mobile Money</span>
                        </motion.button>
                    </div>
                </div>

                {/* Bank Account Information */}
                {formData.account_type === 'bank' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4 p-6 bg-teal-50 rounded-xl border border-teal-200"
                    >
                        <h4 className="text-lg font-semibold text-teal-800 flex items-center gap-2">
                            <Building2 className="w-5 h-5" />
                            Bank Account Information
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Bank Name <span className="text-red-500">*</span>
                                </label>
                                <select 
                                    name="bank_name" 
                                    value={formData.bank_name} 
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                    required
                                >
                                    <option value="">Select a bank</option>
                                    {banksInUganda.map(bank =>
                                        <option key={bank} value={bank}>{bank}</option>
                                    )}
                                </select>
                            </div>
                            
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Bank Branch
                                </label>
                                <input
                                    type="text"
                                    name="bank_branch"
                                    value={formData.bank_branch}
                                    onChange={handleInputChange}
                                    placeholder="Enter bank branch"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                />
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Account Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="account_number"
                                    value={formData.account_number}
                                    onChange={handleInputChange}
                                    placeholder="Enter account number"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                    required
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Account Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="account_name"
                                    value={formData.account_name}
                                    onChange={handleInputChange}
                                    placeholder="Enter account name"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all duration-200"
                                    required
                                />
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Mobile Money Information */}
                {formData.account_type === 'mobile_money' && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-4 p-6 bg-orange-50 rounded-xl border border-orange-200"
                    >
                        <h4 className="text-lg font-semibold text-orange-800 flex items-center gap-2">
                            <Smartphone className="w-5 h-5" />
                            Mobile Money Information
                        </h4>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Network Name <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="network_name"
                                    value={formData.network_name}
                                    onChange={handleInputChange}
                                    placeholder="e.g., MTN, Airtel, etc."
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                                    required
                                />
                            </div>
                            
                            <div className="space-y-2">
                                <label className="block text-sm font-medium text-gray-700">
                                    Contact Number <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    name="contact"
                                    value={formData.contact}
                                    onChange={handleInputChange}
                                    placeholder="Enter contact number"
                                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-200"
                                    required
                                />
                            </div>
                        </div>
                    </motion.div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-gray-200">
                    <motion.button
                        type="submit"
                        className="flex-1 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 !text-white px-8 py-4 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-3 shadow-lg hover:shadow-xl transform hover:scale-102"
                        whileHover={{ scale: 1.005 }}
                        whileTap={{ scale: 0.98 }}
                    >
                        {editing ? (
                            <>
                                <Edit className="w-5 h-5" />
                                Update Account
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Save Account
                            </>
                        )}
                    </motion.button>
                    
                    {editing && (
                        <motion.button
                            type="button"
                            onClick={onCancel}
                            className="px-8 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-all duration-200 flex items-center gap-3 transform hover:scale-105"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                        >
                            <X className="w-5 h-5" />
                            Cancel Edit
                        </motion.button>
                    )}
                </div>
            </form>
        </div>
    );
};

export default PaymentDetailsForm;
